import {
  ReactElement,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import type { ListImperativeAPI } from 'react-window'
import { Box, Typography } from '@mui/material'
import { NRowTabHeader, TextTabs } from '@ui2/navigation'
import CommentsListPane from '../CommentsListPane'
import ImageCardsListPane from '../ImageCardsListPane'
import TablesListPane from '../TablesListPane'
import ElementListSearchBar from './components/ElementListSearchBar'
import StoreyFilterButton from './components/StoreyFilterButton'
import VirtualizedElementList, { ElementListRowProps } from './components/VirtualizedElementList'
import {
  COMMENTS_TAB_ID,
  ELEMENT_LIST_TAB_LABEL_WIDTH_THRESHOLD,
  IMAGE_CARDS_TAB_ID,
  TABLES_TAB_ID,
} from './constants'
import useListMultiSelect from './hooks/useListMultiSelect'
import useScrollSelectedIntoView from './hooks/useScrollSelectedIntoView'
import { ElementListItem, ElementListTab } from './types'
import { filterElementListItems } from './utils/filterElementListItems'
import { collectStoreys } from './utils/storeys'

const EMPTY_SELECTED: string[] = []

interface ActiveTab {
  primaryId: string
  subTabId: string
}

export interface ElementListPaneProps {
  tabs: ElementListTab[]
  /** Items keyed by element type (as produced by useElementListItems). */
  itemsByElementType: Record<string, ElementListItem[]>
  selectedIds?: string[]
  /** Omitting this makes the list display-only (rows render but clicks do nothing). */
  onSelectionChange?: (ids: string[]) => void
  emptyMessage?: string
  /** Notifies the page which primary tab is active, so it can swap the adjacent drawer (e.g. show
   *  the CommentDrawer while the "Kommentare" tab is selected). */
  onActivePrimaryChange?: (primaryId: string) => void
}

/**
 * Selection-source-agnostic element list: primary + secondary tabs (never mixing element types in
 * one list) over a virtualized, optionally multi-selectable list. Owns its tab navigation and
 * follows the current selection's category. Shared across StructuralPlanning, GlobalLoads (and
 * future pages); pages pass the items and bind selection to their own store / query params.
 */
const ElementListPane = ({
  tabs,
  itemsByElementType,
  selectedIds = EMPTY_SELECTED,
  onSelectionChange,
  emptyMessage = 'Keine Elemente',
  onActivePrimaryChange,
}: ElementListPaneProps): ReactElement => {
  const selectable = !!onSelectionChange

  const [active, setActive] = useState<ActiveTab>(() => ({
    primaryId: tabs[0].id,
    subTabId: tabs[0].subTabs[0].id,
  }))

  const activePrimary = tabs.find(tab => tab.id === active.primaryId) ?? tabs[0]
  const activeSubTab =
    activePrimary.subTabs.find(subTab => subTab.id === active.subTabId) ?? activePrimary.subTabs[0]

  const isCommentsTab = activePrimary.id === COMMENTS_TAB_ID
  const isTablesTab = activePrimary.id === TABLES_TAB_ID
  const isImageCardsTab = activePrimary.id === IMAGE_CARDS_TAB_ID

  useEffect(() => {
    onActivePrimaryChange?.(activePrimary.id)
  }, [activePrimary.id, onActivePrimaryChange])

  const onPrimaryChange = useCallback(
    (primaryId: string) => {
      const primary = tabs.find(tab => tab.id === primaryId)
      if (primary) setActive({ primaryId, subTabId: primary.subTabs[0].id })
    },
    [tabs],
  )

  const onSubTabChange = useCallback(
    (subTabId: string) => setActive(prev => ({ ...prev, subTabId })),
    [],
  )

  // Map element type -> its tab location, so the pane can reveal the selected element's category.
  const locationByElementType = useMemo(() => {
    const map: Record<string, ActiveTab> = {}
    tabs.forEach(tab =>
      tab.subTabs.forEach(subTab =>
        subTab.elementTypes.forEach(elementType => {
          map[elementType] = { primaryId: tab.id, subTabId: subTab.id }
        }),
      ),
    )
    return map
  }, [tabs])

  const elementTypeById = useMemo(() => {
    const map: Record<string, ElementTypes> = {}
    Object.values(itemsByElementType).forEach(items =>
      items.forEach(item => {
        map[item.id] = item.elementType
      }),
    )
    return map
  }, [itemsByElementType])

  // Follow scene/drawer selection: surface the tab holding the selected element so its row scrolls
  // into view (mirrors Results2 auto-switching tabs on a 3D click). Only re-navigates when the
  // selected element's type changes, so manual tab browsing while selected is preserved.
  const firstSelectedType = selectedIds[0] ? elementTypeById[selectedIds[0]] : undefined
  const revealLocation = firstSelectedType ? locationByElementType[firstSelectedType] : undefined
  const revealKey = revealLocation ? `${revealLocation.primaryId}:${revealLocation.subTabId}` : null
  const lastRevealKeyRef = useRef<string | null>(null)
  if (revealLocation && revealKey !== lastRevealKeyRef.current) {
    lastRevealKeyRef.current = revealKey
    if (
      revealLocation.primaryId !== active.primaryId ||
      revealLocation.subTabId !== active.subTabId
    ) {
      setActive(revealLocation)
    }
  }

  const allItems = useMemo(
    () => activeSubTab.elementTypes.flatMap(elementType => itemsByElementType[elementType] ?? []),
    [activeSubTab, itemsByElementType],
  )

  // Storeys come from the whole model (all tabs) so the filter is stable while switching tabs.
  const availableStoreys = useMemo(() => collectStoreys(itemsByElementType), [itemsByElementType])
  const [storeyFilter, setStoreyFilter] = useState<string[]>([])

  const [searchText, setSearchText] = useState('')
  const items = useMemo(() => {
    const searchMatched = filterElementListItems(allItems, searchText)
    return storeyFilter.length === 0
      ? searchMatched
      : searchMatched.filter(item => !!item.storey && storeyFilter.includes(item.storey))
  }, [allItems, searchText, storeyFilter])

  const hasActiveFilter = !!searchText || storeyFilter.length > 0

  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds])
  const listRef = useRef<ListImperativeAPI | null>(null)

  const { handleRowClick } = useListMultiSelect({ items, selectedIds, onSelectionChange })
  const { handleRowsRendered } = useScrollSelectedIntoView({ items, selectedIds, listRef })

  const rowProps = useMemo<ElementListRowProps>(
    () => ({ items, selectedIdSet, selectable, onRowClick: handleRowClick }),
    [items, selectedIdSet, selectable, handleRowClick],
  )

  // Fall back to short tab labels when the pane is too narrow for the full ones.
  const rootRef = useRef<HTMLDivElement | null>(null)
  const [showLabels, setShowLabels] = useState(true)
  useLayoutEffect(() => {
    const element = rootRef.current
    if (!element) return
    const update = () =>
      setShowLabels(prev => {
        const next = element.clientWidth >= ELEMENT_LIST_TAB_LABEL_WIDTH_THRESHOLD
        return prev === next ? prev : next
      })
    update()
    const observer = new ResizeObserver(update)
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  return (
    <Box
      ref={rootRef}
      data-cy="element-list-pane"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        backgroundColor: 'common.white',
      }}
    >
      <NRowTabHeader
        value={activePrimary.id}
        onChange={onPrimaryChange}
        showLabels={showLabels}
        rows={2}
        items={tabs.map(tab => ({
          id: tab.id,
          label: tab.label,
          shortLabel: tab.shortLabel,
          shortIcon: tab.shortIcon,
        }))}
      />
      {activePrimary.subTabs.length > 1 && (
        <TextTabs
          value={activeSubTab.id}
          onChange={onSubTabChange}
          showLabels={showLabels}
          items={activePrimary.subTabs.map(subTab => ({
            id: subTab.id,
            label: subTab.label,
            shortLabel: subTab.shortLabel,
            shortIcon: subTab.shortIcon,
          }))}
        />
      )}
      {isCommentsTab ? (
        <CommentsListPane />
      ) : isTablesTab ? (
        <TablesListPane />
      ) : isImageCardsTab ? (
        <ImageCardsListPane />
      ) : (
        <>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 1,
              py: 0.75,
              borderBottom: theme => `1px solid ${theme.palette.divider}`,
            }}
          >
            {availableStoreys.length > 1 && (
              <StoreyFilterButton
                storeys={availableStoreys}
                selected={storeyFilter}
                onChange={setStoreyFilter}
              />
            )}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <ElementListSearchBar value={searchText} onChange={setSearchText} />
            </Box>
          </Box>
          {items.length === 0 ? (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 2,
              }}
            >
              <Typography variant="body2" color="text.secondary" data-cy="element-list-empty">
                {hasActiveFilter ? 'Keine Treffer' : emptyMessage}
              </Typography>
            </Box>
          ) : (
            <VirtualizedElementList
              listRef={listRef}
              rowProps={rowProps}
              onRowsRendered={handleRowsRendered}
            />
          )}
        </>
      )}
    </Box>
  )
}

export default ElementListPane

export { default as TwoPaneElementDrawer } from './TwoPaneElementDrawer'
export { default as useElementListItems } from './hooks/useElementListItems'
export {
  COMMENTS_ELEMENT_LIST_TAB,
  COMMENTS_TAB_ID,
  DEFAULT_ELEMENT_LIST_TABS,
  ELEMENT_LIST_PANE_DEFAULT_WIDTH,
  ELEMENT_LIST_PANE_MIN_WIDTH,
  IMAGE_CARDS_ELEMENT_LIST_TAB,
  IMAGE_CARDS_TAB_ID,
  TABLES_ELEMENT_LIST_TAB,
  TABLES_TAB_ID,
} from './constants'
export type { ElementListItem, ElementListSubTab, ElementListTab } from './types'
